import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Download, Filter as FilterIcon } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Reusable drill-down side sheet with filterable, sortable table.
 *
 * Props:
 *  - title, subtitle
 *  - endpoint: string (e.g. '/admin/drill/learners')
 *  - filters: list of { key, label, type: 'text'|'select', options? } — options can be {value,label}[]
 *  - columns: list of { key, label, render?, align?, sortable? }
 *  - initialFilters: object
 *  - onClose
 *  - downloadFilename
 */
export default function DrillSheet({
    title,
    subtitle,
    endpoint,
    filters = [],
    columns = [],
    initialFilters = {},
    onClose,
    downloadFilename = 'export.csv',
    rowKey = 'id',
}) {
    const [data, setData] = useState({ total: 0, rows: [] });
    const [loading, setLoading] = useState(true);
    const [activeFilters, setActiveFilters] = useState(initialFilters);
    const [sort, setSort] = useState({ key: null, dir: 1 });
    const [search, setSearch] = useState('');

    const load = () => {
        setLoading(true);
        const params = { ...activeFilters };
        Object.keys(params).forEach((k) => { if (params[k] === '' || params[k] == null) delete params[k]; });
        api.get(endpoint, { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [JSON.stringify(activeFilters)]);

    const setFilter = (k, v) => setActiveFilters({ ...activeFilters, [k]: v });

    let rows = data.rows || [];
    if (search) {
        const s = search.toLowerCase();
        rows = rows.filter((r) => Object.values(r).some((v) => String(v ?? '').toLowerCase().includes(s)));
    }
    if (sort.key) {
        rows = [...rows].sort((a, b) => {
            const av = a[sort.key], bv = b[sort.key];
            if (av == null) return 1; if (bv == null) return -1;
            if (typeof av === 'number') return (av - bv) * sort.dir;
            return String(av).localeCompare(String(bv)) * sort.dir;
        });
    }

    const downloadCSV = () => {
        const header = columns.map((c) => c.label).join(',') + '\n';
        const body = rows.map((r) => columns.map((c) => {
            const v = r[c.key];
            return `"${String(v ?? '').replace(/"/g, '""')}"`;
        }).join(',')).join('\n');
        const blob = new Blob([header + body], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = downloadFilename; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end"
                onClick={onClose}
                data-testid="drill-sheet"
            >
                <motion.div
                    initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 28, stiffness: 280 }}
                    className="w-full max-w-5xl h-full bg-white shadow-2xl flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-start justify-between p-5 border-b border-slate-200">
                        <div>
                            <h2 className="font-display font-extrabold text-2xl text-slate-900">{title}</h2>
                            {subtitle && <div className="text-sm text-slate-500 mt-0.5">{subtitle}</div>}
                            <div className="text-xs text-slate-500 mt-2">{data.total} total record{data.total === 1 ? '' : 's'}{loading && ' · loading…'}</div>
                        </div>
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="drill-close-btn">
                            <X className="h-5 w-5 text-slate-600" />
                        </button>
                    </div>

                    {/* Filters */}
                    {(filters.length > 0 || true) && (
                        <div className="p-4 border-b border-slate-100 bg-slate-50">
                            <div className="flex flex-wrap items-center gap-2">
                                <FilterIcon className="h-3.5 w-3.5 text-slate-500" />
                                <div className="relative flex-1 min-w-[200px]">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                    <input
                                        value={search} onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search…" className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                                        data-testid="drill-search"
                                    />
                                </div>
                                {filters.map((f) => (
                                    f.type === 'select' ? (
                                        <select
                                            key={f.key}
                                            value={activeFilters[f.key] || ''}
                                            onChange={(e) => setFilter(f.key, e.target.value)}
                                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white capitalize"
                                            data-testid={`drill-filter-${f.key}`}
                                        >
                                            <option value="">All {f.label}</option>
                                            {(f.options || []).map((o) => (
                                                <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            key={f.key}
                                            value={activeFilters[f.key] || ''}
                                            onChange={(e) => setFilter(f.key, e.target.value)}
                                            placeholder={f.label}
                                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                                            data-testid={`drill-filter-${f.key}`}
                                        />
                                    )
                                ))}
                                <button onClick={() => { setActiveFilters({}); setSearch(''); }} className="text-xs text-slate-600 hover:text-slate-900 ml-1" data-testid="drill-clear-btn">Clear</button>
                                <button onClick={downloadCSV} className="ml-auto inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-brand-black text-brand-yellow text-xs font-semibold hover:bg-[#0F0D0E]" data-testid="drill-export-csv">
                                    <Download className="h-3.5 w-3.5" /> CSV
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    <div className="flex-1 overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-white border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
                                <tr>
                                    {columns.map((c) => (
                                        <th
                                            key={c.key}
                                            onClick={() => c.sortable !== false && setSort({ key: c.key, dir: sort.key === c.key ? -sort.dir : 1 })}
                                            className={`py-3 px-4 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.sortable !== false ? 'cursor-pointer hover:text-slate-900' : ''}`}
                                            data-testid={`drill-col-${c.key}`}
                                        >
                                            {c.label}{sort.key === c.key && <span className="ml-1">{sort.dir > 0 ? '↑' : '↓'}</span>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r) => (
                                    <tr key={r[rowKey] ?? r.user_id ?? r.enrollment_id ?? r.certificate_id ?? JSON.stringify(r)} className="hover:bg-slate-50" data-testid={`drill-row-${r[rowKey] ?? r.user_id ?? r.enrollment_id ?? r.certificate_id ?? ''}`}>
                                        {columns.map((c) => (
                                            <td key={c.key} className={`py-2.5 px-4 ${c.align === 'right' ? 'text-right' : ''}`}>
                                                {c.render ? c.render(r) : (r[c.key] ?? '—')}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {!rows.length && !loading && (
                                    <tr><td colSpan={columns.length} className="py-12 text-center text-slate-500 text-sm">No records match these filters.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
